import { OPTIONS_CONTENT_TYPE } from 'components/ContentReporting/shared-constants';
import { CONTENT_TYPES } from 'components/ContentReporting/content-types';
import { ContentType, Reason } from 'proto/generated/tintin_content_policy';
import { EntityType } from 'proto/generated/inspector_gadget';

export enum Field {
  REPORTER_EMAIL = 'reporterEmail',
  REPORTED_CONTENT_URIS = 'productUris',
  REPORTED_CONTENT_TYPE = 'contentType',
  REPORTER_COUNTRY_CODE = 'reporterCountryCode',
  VERIFICATION_RESOURCE_URI = 'resourceUri',
  VERIFICATION_CAPTCHA_TOKEN = 'captchaToken',
  VERIFICATION_CODE = 'code',
  // Extra fields for employees
  TEAM = 'team',
  TEAM_OTHER = 'teamOther',
  URGENCY = 'urgency',
  ADDITIONAL_INFO = 'additionalInfo',
  REASON = 'reason',
}

export enum OptionsUrgency {
  URGENCY_INVALID = 'URGENCY_INVALID',
  URGENCY_HIGH = 'URGENCY_HIGH',
  URGENCY_MEDIUM = 'URGENCY_MEDIUM',
  URGENCY_LOW = 'URGENCY_LOW',
}

export enum OptionsTeam {
  CREATOR_SUPPORT = 'creator_support',
  CUSTOMER_SERVICE = 'customer_service',
  PR_COMMS = 'pr_comms',
  TRUST_AND_SAFETY = 'trust_and_safety',
  ALM = 'alm',
  LEGAL = 'legal',
  CONTENT_PROTECTION = 'content_protection',
  OTHER = 'other',
}

interface FieldProperty {
  label: string;
  translationPrefix?: string;
}

export const FieldProperties: Record<Field, FieldProperty> = {
  [Field.REPORTER_EMAIL]: {
    label: 'reporter_email_label',
  },
  [Field.REPORTED_CONTENT_URIS]: {
    label: 'content_uri_label',
  },
  [Field.REPORTED_CONTENT_TYPE]: {
    label: 'content_type_label',
    translationPrefix: 'content_type',
  },
  [Field.REPORTER_COUNTRY_CODE]: {
    label: '',
  },
  [Field.VERIFICATION_RESOURCE_URI]: {
    label: 'resource_uri',
  },
  [Field.VERIFICATION_CODE]: {
    label: 'code',
  },
  [Field.VERIFICATION_CAPTCHA_TOKEN]: {
    label: 'token',
  },
  [Field.TEAM]: {
    label: 'team_label',
    translationPrefix: 'team',
  },
  [Field.TEAM_OTHER]: {
    label: 'team_other_label',
  },
  [Field.URGENCY]: {
    label: 'urgency_label',
  },
  [Field.ADDITIONAL_INFO]: {
    label: 'additional_info_label',
  },
  [Field.REASON]: {
    label: 'report_reason_label',
    translationPrefix: 'report_reason',
  },
};

export interface ReasonOption {
  value: Reason;
  label: string;
}

export const ReasonOptions: Array<ReasonOption> = [
  {
    value: Reason.REASON_SEXUAL,
    label: 'content_reporting_intro_page_reason_sexual_content',
  },
  {
    value: Reason.REASON_VIOLENT_OR_DANGEROUS,
    label: 'content_reporting_intro_page_reason_violent_or_dangerous',
  },
  {
    value: Reason.REASON_HATEFUL_OR_ABUSIVE,
    label: 'content_reporting_intro_page_reason_hateful_or_abusive',
  },
  {
    value: Reason.REASON_DECEPTIVE,
    label: 'content_reporting_intro_page_reason_deceptive_content',
  },
  {
    value: Reason.REASON_DO_NOT_LIKE_IT,
    label: 'content_reporting_intro_page_reason_do_not_like',
  },
];

export const UrgencyOptions: Array<OptionsUrgency> = [
  OptionsUrgency.URGENCY_HIGH,
  OptionsUrgency.URGENCY_MEDIUM,
  OptionsUrgency.URGENCY_LOW,
];

export const TeamOptions: Array<OptionsTeam> = [
  OptionsTeam.CREATOR_SUPPORT,
  OptionsTeam.CUSTOMER_SERVICE,
  OptionsTeam.PR_COMMS,
  OptionsTeam.TRUST_AND_SAFETY,
  OptionsTeam.ALM,
  OptionsTeam.LEGAL,
  OptionsTeam.CONTENT_PROTECTION,
  OptionsTeam.OTHER,
];

export const REPORTED_CONTENT_TYPE_OPTIONS: Array<OPTIONS_CONTENT_TYPE> = [
  OPTIONS_CONTENT_TYPE.USER_IMAGE,
  OPTIONS_CONTENT_TYPE.PLAYLIST_IMAGE,
  OPTIONS_CONTENT_TYPE.PLAYLIST_TITLE,
  OPTIONS_CONTENT_TYPE.PLAYLIST_DESCRIPTION,
  OPTIONS_CONTENT_TYPE.ARTIST_PROFILE_IMAGE,
  OPTIONS_CONTENT_TYPE.ARTIST_BANNER_IMAGE,
  OPTIONS_CONTENT_TYPE.ARTIST_BIO_TEXT,
  OPTIONS_CONTENT_TYPE.ARTIST_PICK_TEXT,
  OPTIONS_CONTENT_TYPE.ARTIST_MERCHANDISE,
  OPTIONS_CONTENT_TYPE.NOW_PLAYING,
];

// the values should be consistent with what is expected in tintin:
// https://ghe.spotify.net/content-toolbox/tintin/blob/master/src/main/proto/tintin_content_policy.proto
export const ALL_FIELDS_IN_PAYLOAD: Array<Field> = [
  Field.REPORTER_EMAIL,
  Field.REPORTED_CONTENT_URIS, // this should be an array of strings called productUris
  Field.REPORTED_CONTENT_TYPE,
  Field.REASON,
  Field.VERIFICATION_RESOURCE_URI,
  Field.VERIFICATION_CODE,
];

export const ALL_FIELDS_IN_EXTERNAL_PAYLOAD: Array<Field> = [
  ...ALL_FIELDS_IN_PAYLOAD,
  Field.VERIFICATION_CAPTCHA_TOKEN,
];

export const ALL_FIELDS_IN_EMPLOYEE_PAYLOAD: Array<Field> = [
  ...ALL_FIELDS_IN_PAYLOAD,
  Field.TEAM,
  Field.URGENCY,
  Field.ADDITIONAL_INFO,
];

// URI and content type configuration for External reports
export const EXTERNAL_ACCEPTED_URI_TYPES: Array<CONTENT_TYPES> = [
  CONTENT_TYPES.USER,
  CONTENT_TYPES.PLAYLIST,
  CONTENT_TYPES.PLAYLIST_OLD,
  CONTENT_TYPES.ARTIST,
  CONTENT_TYPES.TRACK,
  CONTENT_TYPES.SHOW,
  CONTENT_TYPES.EPISODE,
  CONTENT_TYPES.ARTIST_VIDEO,
  CONTENT_TYPES.POLL,
  CONTENT_TYPES.QUESTION,
  CONTENT_TYPES.RESPONSE,
];

export const EXTERNAL_CONTENT_URI_TO_CONTENT_TYPE_INPUT_ACCEPTED_CONTENT_TYPES: Record<
  EntityType,
  Array<ContentType>
> = {
  [EntityType.ENTITY_TYPE_UNKNOWN]: [],
  [EntityType.ENTITY_TYPE_AUDIOBOOK]: [
    ContentType.CONTENT_TYPE_AUDIOBOOK_NAME,
    ContentType.CONTENT_TYPE_AUDIOBOOK_ARTWORK,
    ContentType.CONTENT_TYPE_AUDIOBOOK_DESCRIPTION,
  ],
  [EntityType.ENTITY_TYPE_AUDIOBOOK_CHAPTER]: [
    ContentType.CONTENT_TYPE_AUDIOBOOK_CHAPTER_DESCRIPTION,
    ContentType.CONTENT_TYPE_AUDIOBOOK_CHAPTER_AUDIO,
    ContentType.CONTENT_TYPE_AUDIOBOOK_CHAPTER_NAME,
  ],
  [EntityType.ENTITY_TYPE_USER]: [ContentType.CONTENT_TYPE_USER_IMAGE],
  [EntityType.ENTITY_TYPE_SHOW]: [
    ContentType.CONTENT_TYPE_ARTWORK,
    ContentType.CONTENT_TYPE_PODCAST_DESCRIPTION,
    ContentType.CONTENT_TYPE_TITLE,
  ],
  [EntityType.ENTITY_TYPE_EPISODE]: [
    ContentType.CONTENT_TYPE_AUDIO,
    ContentType.CONTENT_TYPE_ARTWORK,
    ContentType.CONTENT_TYPE_PODCAST_DESCRIPTION,
    ContentType.CONTENT_TYPE_TITLE,
  ],
  [EntityType.ENTITY_TYPE_ALBUM]: [],
  [EntityType.ENTITY_TYPE_TRACK]: [ContentType.CONTENT_TYPE_NOW_PLAYING],
  [EntityType.ENTITY_TYPE_PLAYLIST]: [
    ContentType.CONTENT_TYPE_PLAYLIST_IMAGE,
    ContentType.CONTENT_TYPE_PLAYLIST_TITLE,
    ContentType.CONTENT_TYPE_PLAYLIST_DESCRIPTION,
  ],
  [EntityType.ENTITY_TYPE_ARTIST]: [
    ContentType.CONTENT_TYPE_ARTIST_BIO_TEXT,
    ContentType.CONTENT_TYPE_ARTIST_PICK_TEXT,
    ContentType.CONTENT_TYPE_ARTIST_PROFILE_IMAGE,
    ContentType.CONTENT_TYPE_ARTIST_BANNER_IMAGE,
    ContentType.CONTENT_TYPE_ARTIST_MERCHANDISE,
  ],
  [EntityType.ENTITY_TYPE_ARTIST_EXPRESSION]: [
    ContentType.CONTENT_TYPE_ARTIST_VIDEO_TITLE,
    ContentType.CONTENT_TYPE_ARTIST_VIDEO_VIDEO,
    ContentType.CONTENT_TYPE_ARTIST_VIDEO_AUDIO,
  ],
  [EntityType.ENTITY_TYPE_ALBUM_PRERELEASE_EXPRESSION]: [
    ContentType.CONTENT_TYPE_ARTIST_VIDEO_TITLE,
    ContentType.CONTENT_TYPE_ARTIST_VIDEO_VIDEO,
    ContentType.CONTENT_TYPE_ARTIST_VIDEO_AUDIO,
  ],
  [EntityType.ENTITY_TYPE_POLL]: [
    ContentType.CONTENT_TYPE_POLL_QUESTION_TEXT,
    ContentType.CONTENT_TYPE_POLL_OPTIONS_TEXT,
  ],
  [EntityType.ENTITY_TYPE_QUESTION]: [ContentType.CONTENT_TYPE_QUESTION_TEXT],
  [EntityType.ENTITY_TYPE_RESPONSE]: [ContentType.CONTENT_TYPE_RESPONSE_TEXT],
};

// URI and content type configuration for External reports
export const EMPLOYEE_ACCEPTED_URI_TYPES: Array<CONTENT_TYPES> = [
  CONTENT_TYPES.USER,
  CONTENT_TYPES.PLAYLIST,
  CONTENT_TYPES.PLAYLIST_OLD,
  CONTENT_TYPES.ARTIST,
  CONTENT_TYPES.TRACK,
  CONTENT_TYPES.ALBUM,
  CONTENT_TYPES.SHOW,
  CONTENT_TYPES.EPISODE,
  CONTENT_TYPES.ARTIST_VIDEO,
  CONTENT_TYPES.POLL,
  CONTENT_TYPES.QUESTION,
  CONTENT_TYPES.RESPONSE,
];

export const EMPLOYEE_CONTENT_URI_TO_CONTENT_TYPE_INPUT_ACCEPTED_CONTENT_TYPES: Record<
  EntityType,
  Array<ContentType>
> = {
  [EntityType.ENTITY_TYPE_UNKNOWN]: [],
  [EntityType.ENTITY_TYPE_AUDIOBOOK]: [
    ContentType.CONTENT_TYPE_AUDIOBOOK_ARTWORK,
    ContentType.CONTENT_TYPE_AUDIOBOOK_NAME,
    ContentType.CONTENT_TYPE_AUDIOBOOK_DESCRIPTION,
  ],
  [EntityType.ENTITY_TYPE_AUDIOBOOK_CHAPTER]: [
    ContentType.CONTENT_TYPE_AUDIOBOOK_CHAPTER_DESCRIPTION,
    ContentType.CONTENT_TYPE_AUDIOBOOK_CHAPTER_AUDIO,
    ContentType.CONTENT_TYPE_AUDIOBOOK_CHAPTER_NAME,
  ],
  [EntityType.ENTITY_TYPE_USER]: [ContentType.CONTENT_TYPE_USER_IMAGE],
  [EntityType.ENTITY_TYPE_SHOW]: [
    ContentType.CONTENT_TYPE_ARTWORK,
    ContentType.CONTENT_TYPE_PODCAST_DESCRIPTION,
    ContentType.CONTENT_TYPE_TITLE,
  ],
  [EntityType.ENTITY_TYPE_EPISODE]: [
    ContentType.CONTENT_TYPE_AUDIO,
    ContentType.CONTENT_TYPE_ARTWORK,
    ContentType.CONTENT_TYPE_PODCAST_DESCRIPTION,
    ContentType.CONTENT_TYPE_TITLE,
  ],
  [EntityType.ENTITY_TYPE_ALBUM]: [ContentType.CONTENT_TYPE_ARTWORK],
  [EntityType.ENTITY_TYPE_TRACK]: [
    ContentType.CONTENT_TYPE_AUDIO,
    ContentType.CONTENT_TYPE_NOW_PLAYING,
    ContentType.CONTENT_TYPE_MUSIC_STORYLINES,
  ],
  [EntityType.ENTITY_TYPE_PLAYLIST]: [
    ContentType.CONTENT_TYPE_PLAYLIST_IMAGE,
    ContentType.CONTENT_TYPE_PLAYLIST_TITLE,
    ContentType.CONTENT_TYPE_PLAYLIST_DESCRIPTION,
  ],
  [EntityType.ENTITY_TYPE_ARTIST]: [
    ContentType.CONTENT_TYPE_ARTIST_BIO_TEXT,
    ContentType.CONTENT_TYPE_ARTIST_PICK_TEXT,
    ContentType.CONTENT_TYPE_ARTIST_PROFILE_IMAGE,
    ContentType.CONTENT_TYPE_ARTIST_BANNER_IMAGE,
    ContentType.CONTENT_TYPE_ARTIST_MERCHANDISE,
  ],
  [EntityType.ENTITY_TYPE_ARTIST_EXPRESSION]: [
    ContentType.CONTENT_TYPE_ARTIST_VIDEO_TITLE,
    ContentType.CONTENT_TYPE_ARTIST_VIDEO_VIDEO,
    ContentType.CONTENT_TYPE_ARTIST_VIDEO_AUDIO,
  ],
  [EntityType.ENTITY_TYPE_ALBUM_PRERELEASE_EXPRESSION]: [
    ContentType.CONTENT_TYPE_ARTIST_VIDEO_TITLE,
    ContentType.CONTENT_TYPE_ARTIST_VIDEO_VIDEO,
    ContentType.CONTENT_TYPE_ARTIST_VIDEO_AUDIO,
  ],
  [EntityType.ENTITY_TYPE_POLL]: [
    ContentType.CONTENT_TYPE_POLL_QUESTION_TEXT,
    ContentType.CONTENT_TYPE_POLL_OPTIONS_TEXT,
  ],
  [EntityType.ENTITY_TYPE_QUESTION]: [ContentType.CONTENT_TYPE_QUESTION_TEXT],
  [EntityType.ENTITY_TYPE_RESPONSE]: [ContentType.CONTENT_TYPE_RESPONSE_TEXT],
};

export const PODCAST_ENTITY_TYPES = [EntityType.ENTITY_TYPE_EPISODE, EntityType.ENTITY_TYPE_SHOW];
